<script setup lang="ts">
  import type { Placement } from '@floating-ui/vue';
  import {
    useFloating,
    offset,
    flip,
    shift,
    arrow,
    autoUpdate,
  } from '@floating-ui/vue';
  import { InformationCircleIcon } from '@heroicons/vue/24/outline'
  import { useBreakpoints, breakpointsTailwind } from '@vueuse/core'

  const props = withDefaults(defineProps<{
    isInteractive?: boolean
    floatingClasses?: string
    placement?: Placement | string
    offsetDistance?: number
    disableOnMobile?: boolean
    disabled?: boolean
  }>(), {
    isInteractive: false,
    floatingClasses: '',
    placement: 'top',
    offsetDistance: 10,
    disableOnMobile: true,
    disabled: false
  })

  const breakpoints = useBreakpoints(breakpointsTailwind)
  const isMobile = breakpoints.smaller('lg')

  const reference = ref<HTMLElement>()
  const floating = ref<HTMLElement>()
  const floatingArrow = ref<HTMLElement>()

  const { floatingStyles, middlewareData } = useFloating(reference, floating, {
    whileElementsMounted: autoUpdate,
    placement: props.placement as Placement,
    middleware: [offset(props.offsetDistance), flip(), shift(), arrow({ element: floatingArrow })],
  })

  const open = ref<boolean>(false);
  let closeTimeout: any = null;

  // Update the position programmatically if needed
  watch(open, () => {
    if (open.value) {
      cancelClose()
      showTooltip()
    } else {
      hideTooltip()
    }
  })

  watch(() => props.disabled, (isDisabled) => {
    if (isDisabled) {
      open.value = false
    }
  })

  function scheduleClose() {
    if (props.isInteractive) {
      closeTimeout = setTimeout(() => {
        open.value = false
      }, 100) // delay before closing the tooltip
    } else {
      open.value = false
    }
  }

  function cancelClose() {
    clearTimeout(closeTimeout)
  }

  function showTooltip() {
    if (floating.value) {
      floating.value.style.display = 'block'
    }
  }

  function hideTooltip() {
    if (floating.value) {
      floating.value.style.display = ''
    }
  }

  function handleMouseOver() {
    // Don't show tooltip if explicitly disabled
    if (props.disabled) {
      return
    }
    // Don't show tooltip on mobile if disabled
    if (props.disableOnMobile && isMobile.value) {
      return
    }
    open.value = true
  }
</script>

<template>
  <span ref="reference" v-bind="$attrs" @mouseover="handleMouseOver" @mouseleave="scheduleClose">
    <slot name="reference">
      <InformationCircleIcon class="inline-block h-4 w-4 text-current" aria-hidden="true" />
    </slot>
  </span>
  <Teleport to="body" v-if="true">
    <div
      ref="floating"
      @mouseenter="isInteractive ? cancelClose() : undefined"
      @mouseleave="isInteractive ? scheduleClose() : undefined"
      :style="floatingStyles"
      :class="floatingClasses"
      class="hidden z-[120] w-max max-w-xs rounded bg-gray-800 px-2 py-1 text-sm font-normal text-gray-200"
      style="text-wrap: wrap;"
      >    
      <slot name="floating">Floating</slot>
      <div ref="floatingArrow" :style="{left: middlewareData.arrow?.x != null ? `${middlewareData.arrow.x}px` : ''}" :class="middlewareData.offset?.placement === 'bottom' ? '-top-1.5' : '-bottom-1.5'" class="absolute w-5 h-5 bg-gray-800 -z-[1] pointer-events-none rotate-45 rounded-[3px]"></div>
    </div>
  </Teleport>
</template>
